import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationOptions } from "@tanstack/react-query";

import { noteKeys } from "@/features/notes/(v2)/api/keyFactory";
import { showToast } from "@/providers/ToastProvider";
import type { UUID } from "@/core/types";

import { cosignatureKeys } from "../keyFactory";
import { cosignDocument, type CosignDocumentVariables } from "../queryFunctions/cosignDocument";

export function useCosignDocument({
  onSuccess,
  ...options
}: UseMutationOptions<void, unknown, CosignDocumentVariables> = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cosignDocument,
    ...options,
    onError: () => {
      showToast([{ heading: "Something went wrong. Please try again.", variant: "error" }]);
    },
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: cosignatureKeys.base });
      await queryClient.invalidateQueries({
        queryKey: noteKeys.details(variables.documentId as UUID),
      });
      await queryClient.invalidateQueries({
        queryKey: noteKeys.summary(variables.patientId as UUID),
      });
      onSuccess?.(data, variables, context);
      showToast([{ heading: "Note successfully co-signed.", variant: "success" }, { delay: 500 }]);
    },
  });
}
